import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { publicApi } from "../hooks/useApi";
import { usePlayer } from "../context/PlayerContext";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";

const SHOW_DELAY_MS = 18000;
const DISMISS_COOLDOWN_DAYS = 14;
const SUBSCRIBED_KEY = "eb_dispatch_subscribed";
const DISMISSED_AT_KEY = "eb_dispatch_dismissed_at";
const SHOWN_SESSION_KEY = "eb_dispatch_shown_session";

const EXCLUDED_PREFIXES = [
  "/checkout", "/payment", "/admin", "/operator", "/account", "/library",
  "/themuscleschool/lessons", "/themuscleschool/dashboard", "/themuscleschool/activate",
  "/vault",
];

function isExcludedRoute(pathname) {
  return EXCLUDED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

function isSuppressed() {
  if (typeof window === "undefined") return true;
  if (sessionStorage.getItem(SHOWN_SESSION_KEY)) return true;
  if (localStorage.getItem(SUBSCRIBED_KEY) === "true") return true;
  const dismissedAt = localStorage.getItem(DISMISSED_AT_KEY);
  if (dismissedAt) {
    const days = (Date.now() - Number(dismissedAt)) / 86400000;
    if (days < DISMISS_COOLDOWN_DAYS) return true;
  }
  return false;
}

export function DispatchPopup() {
  const location = useLocation();
  const player = usePlayer();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const playingRef = useRef(false);
  const lastFocusedRef = useRef(null);
  playingRef.current = Boolean(player?.playing);

  useEffect(() => {
    if (isSuppressed()) return undefined;
    const timer = setTimeout(() => {
      if (isSuppressed()) return;
      if (isExcludedRoute(window.location.pathname)) return;
      if (playingRef.current) return;
      lastFocusedRef.current = document.activeElement;
      setOpen(true);
      sessionStorage.setItem(SHOWN_SESSION_KEY, "1");
    }, SHOW_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next && !done) {
      localStorage.setItem(DISMISSED_AT_KEY, String(Date.now()));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !consent || submitting) return;
    setError("");
    setSubmitting(true);
    try {
      await publicApi.post("/leads", null, {
        params: {
          email, source: "newsletter", first_name: firstName, consent: true,
          signup_surface: "dispatch_popup", page_path: location.pathname,
          referrer: (typeof document !== "undefined" && document.referrer) || "",
        },
      });
      setDone(true);
      localStorage.setItem(SUBSCRIBED_KEY, "true");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="dispatch-popup"
        className="bg-black border-white/15 text-white max-w-md p-0 overflow-hidden gap-0 max-h-[90vh] overflow-y-auto"
        onCloseAutoFocus={(e) => {
          if (lastFocusedRef.current && document.body.contains(lastFocusedRef.current)) {
            e.preventDefault();
            lastFocusedRef.current.focus();
          }
        }}
      >
        <div className="relative w-full h-32 sm:h-40 overflow-hidden shrink-0">
          <img src="/dispatch/space-explorer.jpg" alt="A lone explorer looking out over an alien ridge at two distant worlds" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-transparent" />
        </div>
        <div className="p-6 sm:p-7">
          <DialogTitle data-testid="dispatch-popup-title" className="font-display text-display-sm uppercase mb-2 text-white tracking-wide">
            The Endure Beyond Dispatch
          </DialogTitle>
          {done ? (
            <p data-testid="dispatch-popup-success" className="text-sm text-brandAccent font-subtitle uppercase tracking-wide py-4">
              You're on the list. Welcome aboard.
            </p>
          ) : (
            <>
              <p className="text-sm text-white/75 mb-5">
                Fresh perspectives on human capability, new publications, and ideas worth exploring—delivered to your inbox.
              </p>
              <form onSubmit={submit} className="space-y-3">
                <input
                  data-testid="dispatch-popup-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  aria-label="Email address, required"
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? "dispatch-popup-error" : undefined}
                  className="w-full bg-white/5 border border-white/20 px-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-brandAccent placeholder:text-white/40"
                />
                <input
                  data-testid="dispatch-popup-firstname-input"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name (optional)"
                  aria-label="First name, optional"
                  className="w-full bg-white/5 border border-white/20 px-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-brandAccent placeholder:text-white/40"
                />
                <label className="flex items-start gap-2.5 text-xs text-white/60 cursor-pointer">
                  <input
                    data-testid="dispatch-popup-consent-checkbox"
                    type="checkbox"
                    required
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5 shrink-0"
                  />
                  <span>
                    I'd like to receive The Dispatch and occasional updates from Endure Beyond Media. See our{" "}
                    <Link to="/privacy" className="underline hover:text-white">Privacy Policy</Link>.{" "}
                    <Link to="/unsubscribe" className="underline hover:text-white">Unsubscribe</Link> anytime.
                  </span>
                </label>
                {error && <p id="dispatch-popup-error" data-testid="dispatch-popup-error" role="alert" aria-live="assertive" className="text-xs text-red-300">{error}</p>}
                <button
                  data-testid="dispatch-popup-submit"
                  type="submit"
                  disabled={!email || !consent || submitting}
                  className="w-full bg-white text-black py-3 text-sm uppercase tracking-wide font-subtitle hover:bg-white/80 transition-colors duration-200 disabled:opacity-40"
                >
                  {submitting ? "Joining…" : "Join the Dispatch"}
                </button>
              </form>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
